import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  applyParamUpdates,
  DASHBOARD_PARAM,
  type ParamUpdates,
} from '../pages/CoachingDashboard/dashboardUrlParams';
import { useAppDispatch } from '../redux/hooks';
import {
  clearOperatorContext,
  PLATFORM_OPERATOR_CONTEXT,
  selectOperatorInstitution,
  selectPlatformMode,
  type OperatorContextSelection,
} from '../redux/operatorContext';

/**
 * The roster and both narrowings belong to the root being left: carried across, they
 * narrow the new context by something outside it and the api refuses the read.
 */
export const ROOT_SCOPED_PARAM_CLEARS = {
  [DASHBOARD_PARAM.rosterId]: null,
  [DASHBOARD_PARAM.institutionId]: null,
  [DASHBOARD_PARAM.kind]: null,
};

/** A caller that knows where it is landing passes `landing`, which is applied after the
 * clears. */
export const useOperatorInstitutionSwitch = (): ((
  selection: OperatorContextSelection,
  landing?: ParamUpdates,
) => void) => {
  const dispatch = useAppDispatch();
  const [, setSearchParams] = useSearchParams();

  return useCallback(
    (selection: OperatorContextSelection, landing?: ParamUpdates) => {
      dispatch(
        selection === PLATFORM_OPERATOR_CONTEXT
          ? selectPlatformMode()
          : selectOperatorInstitution(selection),
      );
      setSearchParams(
        (prev) => applyParamUpdates(prev, { ...ROOT_SCOPED_PARAM_CLEARS, ...landing }),
        { replace: true },
      );
    },
    [dispatch, setSearchParams],
  );
};

/** The way out of any selection, back to the context the api resolves with no header. */
export const useClearOperatorContext = (): (() => void) => {
  const dispatch = useAppDispatch();
  const [, setSearchParams] = useSearchParams();

  return useCallback(() => {
    dispatch(clearOperatorContext());
    setSearchParams((prev) => applyParamUpdates(prev, ROOT_SCOPED_PARAM_CLEARS), {
      replace: true,
    });
  }, [dispatch, setSearchParams]);
};
